import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { submitContact } from "@/lib/api";

// Base + per-user + per-module values (rough public estimates in USD, admin-editable via settings.quote_*)
const PROJECT_TYPES = [
  { key: "hotel", base: 6000, perUser: 60, perModule: 800, weeks: [6, 10] },
  { key: "erp", base: 8000, perUser: 90, perModule: 1200, weeks: [8, 14] },
  { key: "scada", base: 12000, perUser: 40, perModule: 2000, weeks: [10, 20] },
  { key: "website", base: 1800, perUser: 0, perModule: 400, weeks: [3, 6] },
  { key: "mobile", base: 5500, perUser: 20, perModule: 900, weeks: [6, 12] },
  { key: "ai", base: 3500, perUser: 25, perModule: 1200, weeks: [4, 10] },
  { key: "security", base: 5000, perUser: 20, perModule: 700, weeks: [4, 8] },
];

const USERS_OPTIONS = [5, 10, 25, 50, 100, 250];
const MODULES_OPTIONS = [1, 2, 3, 5, 8];

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));

export default function QuoteCalculator() {
  const { t, lang } = useLang();
  const { settings } = useSite();
  const [typeKey, setTypeKey] = useState(PROJECT_TYPES[1].key);
  const [users, setUsers] = useState(10);
  const [modules, setModules] = useState(2);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const typeCfg = useMemo(() => {
    const def = PROJECT_TYPES.find((p) => p.key === typeKey);
    if (!def) return null;
    const num = (v, fb) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fb;
    };
    return {
      ...def,
      base: num(settings[`quote_${typeKey}_base`], def.base),
      perUser: num(settings[`quote_${typeKey}_per_user`], def.perUser),
      perModule: num(settings[`quote_${typeKey}_per_module`], def.perModule),
    };
  }, [typeKey, settings]);
  const typeLabels = t.quote.types;

  const price = useMemo(() => {
    if (!typeCfg) return { low: 0, high: 0 };
    const base = typeCfg.base + users * typeCfg.perUser + modules * typeCfg.perModule;
    return { low: base * 0.85, high: base * 1.25 };
  }, [typeCfg, users, modules]);

  const weeks = typeCfg?.weeks || [4, 8];

  const submit = async (e) => {
    e.preventDefault();
    if (!contactPhone.trim() || contactPhone.trim().length < 7) {
      toast.error(t.quote.errPhone);
      return;
    }
    setSending(true);
    try {
      const summary = t.quote.leadTemplate
        .replace("{type}", typeLabels[typeKey])
        .replace("{users}", users)
        .replace("{modules}", modules)
        .replace("{low}", fmt(price.low))
        .replace("{high}", fmt(price.high))
        .replace("{w1}", weeks[0])
        .replace("{w2}", weeks[1]);
      await submitContact({
        name: contactName || t.quote.anonName,
        phone: contactPhone,
        email: contactEmail || undefined,
        service: typeLabels[typeKey],
        message: summary,
        type: "quote",
        lang,
      });
      setSent(true);
      toast.success(t.quote.success);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 429 ? t.quote.rateLimited : t.quote.error);
    } finally { setSending(false); }
  };

  return (
    <section id="quote-calc" className="relative section-pad" data-testid="quote-calc-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 30%, rgba(0,242,96,0.06), transparent 55%)" }} />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center mb-12">
          <Reveal><SectionTag>{t.quote.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 flex items-center justify-center gap-3">
              <Calculator className="text-primary" size={36} /> {t.quote.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{t.quote.subtitle}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="card-surface rounded-3xl p-6 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-32 -end-32 h-72 w-72 rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,242,96,0.5), transparent 70%)" }} />
            <div className="relative grid lg:grid-cols-5 gap-8">
              {/* Left: choices */}
              <div className="lg:col-span-3 space-y-6">
                <div>
                  <label className="block text-sm font-bold mb-3">{t.quote.chooseType}</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {PROJECT_TYPES.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setTypeKey(p.key)}
                        className={`rounded-xl px-3 py-3 text-sm font-semibold border transition-all ${typeKey === p.key ? "text-[#04120a]" : "border-white/10 hover:border-[color:var(--primary)] text-slate-300"}`}
                        style={typeKey === p.key ? { background: "var(--primary)", borderColor: "var(--primary)" } : {}}
                        data-testid={`quote-type-${p.key}`}
                      >
                        {typeLabels[p.key]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2">{t.quote.users}: <span className="text-primary">{users}</span></label>
                    <div className="flex flex-wrap gap-2">
                      {USERS_OPTIONS.map((u) => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setUsers(u)}
                          className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${users === u ? "text-[#04120a] border-transparent" : "border-white/15 text-slate-300"}`}
                          style={users === u ? { background: "var(--primary)" } : {}}
                          data-testid={`quote-users-${u}`}
                        >{u}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">{t.quote.modules}: <span className="text-primary">{modules}</span></label>
                    <div className="flex flex-wrap gap-2">
                      {MODULES_OPTIONS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setModules(m)}
                          className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${modules === m ? "text-[#04120a] border-transparent" : "border-white/15 text-slate-300"}`}
                          style={modules === m ? { background: "var(--primary)" } : {}}
                          data-testid={`quote-modules-${m}`}
                        >{m}</button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <h4 className="font-bold mb-3 text-sm">{t.quote.getPersonalized}</h4>
                  <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
                    <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder={t.contact.fields.name} className="rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="quote-name" />
                    <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder={t.contact.fields.phone} dir="ltr" required className="rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="quote-phone" />
                    <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder={t.contact.fields.email} dir="ltr" className="sm:col-span-2 rounded-xl bg-[#0d1836] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]" data-testid="quote-email" />
                    <button
                      type="submit"
                      disabled={sending || sent}
                      className="sm:col-span-2 rounded-full px-6 py-3 text-sm font-bold text-[#04120a] disabled:opacity-60 inline-flex items-center justify-center gap-2"
                      style={{ background: "var(--primary)" }}
                      data-testid="quote-submit"
                    >
                      {sent ? <><CheckCircle2 size={16} /> {t.quote.sentBadge}</> : sending ? <><Loader2 size={16} className="animate-spin" /> {t.quote.sending}</> : <><Send size={16} /> {t.quote.sendCta}</>}
                    </button>
                  </form>
                </div>
              </div>

              {/* Right: estimate card */}
              <div className="lg:col-span-2">
                <div className="sticky top-24 rounded-3xl p-7 border-2 border-[color:var(--primary)]/40 relative overflow-hidden" style={{ background: "linear-gradient(160deg, rgba(0,242,96,0.12), rgba(0,242,96,0.02))" }}>
                  <div className="absolute -top-16 -end-16 h-40 w-40 rounded-full opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,242,96,0.6), transparent 70%)" }} />
                  <div className="relative">
                    <div className="text-xs font-bold text-primary uppercase tracking-widest">{t.quote.estimate}</div>
                    <motion.div
                      key={`${typeKey}-${users}-${modules}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="mt-3"
                    >
                      <div className="text-3xl sm:text-4xl font-extrabold" data-testid="quote-price-range">
                        ${fmt(price.low)}<span className="text-slate-500 mx-2">–</span>${fmt(price.high)}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{t.quote.usd}</div>
                    </motion.div>
                    <div className="mt-6 space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">{t.quote.type}</span>
                        <span className="font-bold">{typeLabels[typeKey]}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">{t.quote.users}</span>
                        <span className="font-bold">{users}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">{t.quote.modules}</span>
                        <span className="font-bold">{modules}</span>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-white/10">
                        <span className="text-slate-400">{t.quote.delivery}</span>
                        <span className="font-bold">{weeks[0]}–{weeks[1]} {t.quote.weeks}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-6 leading-relaxed">{t.quote.disclaimer}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
